import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { useState } from "react";

type Partner = {
  name: string;
  sub: string;
  /** Direct logo image URL (preferred). */
  src?: string;
  /** simpleicons.org slug; rendered as white-on-transparent SVG. */
  slug?: string;
  /** Optional background colour behind the logo (e.g. white for dark logos). */
  bg?: string;
};

export const partners: Partner[] = [
  { name: "NVIDIA Inception", sub: "AI Accelerator", slug: "nvidia" },
  {
    name: "NSRCEL · IIM Bangalore",
    sub: "Incubated",
    src: "https://nsrcel.org/wp-content/uploads/2021/06/NSRCEL-Logo.png",
    bg: "#ffffff",
  },
  { name: "Google Cloud for Startups", sub: "Cloud Infrastructure", slug: "googlecloud" },
  { name: "Microsoft for Startups", sub: "Founders Hub", slug: "microsoft" },
  { name: "AWS Activate", sub: "Cloud Credits", slug: "amazonwebservices" },
  { name: "Zoho for Startups", sub: "Enterprise Platform", slug: "zoho" },
  { name: "ElevenLabs", sub: "Voice AI", slug: "elevenlabs" },
  {
    name: "Lovable.dev",
    sub: "AI Development",
    src: "https://lovable.dev/favicon.ico",
  },
  { name: "ChatGPT", sub: "AI Assistant", slug: "openai" },
  { name: "Google Gemini", sub: "Multimodal AI", slug: "googlegemini" },
];

const ELECTRIC = "#0205F4";
const VIOLET = "#5b21b6";
const CYAN = "#22d3ee";
const WARM = "#ff5f1f";
const PINK = "#ec4899";

const gradientGlow = `linear-gradient(135deg, ${VIOLET} 0%, ${ELECTRIC} 55%, ${CYAN} 100%)`;
const gradientWarm = `linear-gradient(135deg, ${WARM} 0%, ${PINK} 100%)`;

const logoUrl = (slug: string) => `https://cdn.simpleicons.org/${slug}/ffffff`;

const PartnerLogo = ({ p }: { p: Partner }) => {
  const [errored, setErrored] = useState(false);
  const imgSrc = p.src ?? (p.slug ? logoUrl(p.slug) : null);
  const showImage = imgSrc && !errored;

  if (showImage) {
    return (
      <div
        className="h-7 w-7 sm:h-8 sm:w-8 rounded-lg flex items-center justify-center shrink-0 overflow-hidden"
        style={p.bg ? { background: p.bg } : undefined}
      >
        <img
          src={imgSrc}
          alt={`${p.name} logo`}
          loading="lazy"
          onError={() => setErrored(true)}
          className="max-h-full max-w-full object-contain"
        />
      </div>
    );
  }

  return (
    <div
      className="h-7 w-7 sm:h-8 sm:w-8 rounded-xl flex items-center justify-center text-white text-xs sm:text-sm font-bold shrink-0"
      style={{ background: gradientGlow }}
      aria-hidden="true"
    >
      {p.name.charAt(0)}
    </div>
  );
};

const Pill = ({ p }: { p: Partner }) => (
  <li className="group relative shrink-0 mx-2 sm:mx-3 list-none">
    <div
      className="absolute -inset-px rounded-2xl opacity-30 blur-md transition-opacity duration-300 group-hover:opacity-70"
      style={{ background: gradientGlow }}
      aria-hidden="true"
    />
    <div className="relative flex items-center gap-2.5 sm:gap-3 rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-md px-4 sm:px-5 py-2 min-w-[180px] sm:min-w-[220px]">
      <PartnerLogo p={p} />
      <div className="flex flex-col leading-tight min-w-0">
        <span className="text-xs sm:text-sm font-semibold text-white whitespace-nowrap">
          {p.name}
        </span>
        <span className="text-[10px] sm:text-[11px] text-white/60 whitespace-nowrap">
          {p.sub}
        </span>
      </div>
    </div>
  </li>
);

export const stats = [
  { k: "10+", v: "Global Partners" },
  { k: "13", v: "Years of Impact" },
  { k: "50K+", v: "Youth Reached" },
  { k: "∞", v: "Possibilities" },
];

export default function TrustRibbon() {
  const loop = [...partners, ...partners];

  return (
    <section
      className="relative overflow-hidden py-8 sm:py-10"
      style={{ background: "#0a0d2b" }}
      aria-labelledby="trust-ribbon-heading"
      role="region"
    >
      {/* ambient glow orbs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-32 -left-32 h-[320px] w-[320px] sm:h-[520px] sm:w-[520px] rounded-full opacity-40 blur-[120px]"
        style={{ background: gradientGlow }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -right-40 h-[320px] w-[320px] sm:h-[520px] sm:w-[520px] rounded-full opacity-30 blur-[140px]"
        style={{ background: gradientWarm }}
      />

      {/* heading */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.6 }}
        className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6 text-center"
      >
        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.05] px-3 sm:px-4 py-1.5 text-[10px] sm:text-xs font-semibold tracking-widest uppercase text-white/80 backdrop-blur">
          <Sparkles className="h-3.5 w-3.5" style={{ color: CYAN }} aria-hidden="true" />
          Trusted Ecosystem
        </div>
        <h2
          id="trust-ribbon-heading"
          className="mt-3 font-display text-xl sm:text-3xl font-bold tracking-tight text-white text-balance"
        >
          Backed by the world's leading{" "}
          <span
            className="bg-clip-text text-transparent"
            style={{ backgroundImage: gradientGlow }}
          >
            AI ecosystem
          </span>
        </h2>
        <p className="mt-2 text-xs sm:text-sm text-white/70 max-w-2xl mx-auto">
          Trusted by industry leaders in{" "}
          <span className="text-white font-semibold">AI, cloud infrastructure</span>
          , and enterprise technology.
        </p>
      </motion.div>

      {/* scrolling ribbon */}
      <div className="relative z-10 mt-5 sm:mt-6 overflow-hidden">
        {/* edge fades */}
        <div className="pointer-events-none absolute inset-y-0 left-0 w-12 sm:w-24 bg-gradient-to-r from-[#0a0d2b] to-transparent z-10" />
        <div className="pointer-events-none absolute inset-y-0 right-0 w-12 sm:w-24 bg-gradient-to-l from-[#0a0d2b] to-transparent z-10" />

        <ul
          className="flex w-max animate-trust-marquee p-0 m-0"
          aria-label="Partner organisations"
        >
          {loop.map((p, i) => (
            <Pill key={`${p.name}-${i}`} p={p} />
          ))}
        </ul>
      </div>

      {/* stats strip */}
      <dl
        className="relative z-10 mx-auto mt-5 sm:mt-6 grid max-w-4xl grid-cols-2 gap-2 sm:gap-3 px-4 sm:px-6 sm:grid-cols-4"
        aria-label="Impact statistics"
      >
        {stats.map((s, i) => (
          <motion.div
            key={s.v}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur px-3 py-2 sm:px-4 sm:py-3 text-center"
          >
            <dt className="sr-only">{s.v}</dt>
            <dd
              className="font-display text-xl sm:text-2xl font-bold bg-clip-text text-transparent"
              style={{ backgroundImage: gradientGlow }}
            >
              {s.k}
            </dd>
            <p className="mt-1 text-[11px] sm:text-sm text-white/70" aria-hidden="true">
              {s.v}
            </p>
          </motion.div>
        ))}
      </dl>

      <style>{`
        @keyframes trust-marquee {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-trust-marquee {
          animation: trust-marquee 40s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-trust-marquee {
            animation: none;
            transform: translateX(0);
          }
        }
      `}</style>
    </section>
  );
}
